import { useState, useRef, useEffect, useCallback } from "react";
import { io } from "socket.io-client";
import Support from "../../Api/Support";
import { onSseNotification } from "../../utils/notificationBus";
import Branches from "../../Api/Branches";

// ── Helpers ──────────────────────────────────────────────────
const COLOR_PALETTE = [
  "#ec4899",
  "#10b981",
  "#8b5cf6",
  "#f59e0b",
  "#0ea5e9",
  "#ef4444",
  "#14b8a6",
  "#f97316",
  "#6366f1",
  "#84cc16",
];

/** Deterministic color from string */
const hashColor = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return COLOR_PALETTE[Math.abs(h) % COLOR_PALETTE.length];
};

/** Initials from name */
const initials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "??";

/** Map API status → dot color */
const statusDotColor = (status, isClose) => {
  if (isClose) return "#d1d5db";
  if (status === "Open") return "#22c55e";
  if (status === "Pending") return "#f59e0b";
  return "#d1d5db";
};

const statusDotLabel = (status, isClose) => {
  if (isClose) return "Closed";
  return status || "Unknown";
};

/** Format ISO time → readable */
const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();

    // Normalize both to midnight for a pure calendar-day comparison
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const diff = Math.round((todayMidnight - dMidnight) / 86400000);

    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const transformResults = (results = []) => {
  const convMap = {}; // hash → { meta, messages[] }

  results.forEach((item) => {
    const key = item.hash;

    if (!convMap[key]) {
      convMap[key] = {
        hash: key,
        clientName: item.clientName || "Unknown",
        concernPerson: item.concernPerson || "",
        latestStatus: item.status,
        latestIsClose: item.isClose,
        messages: [],
        latestTime: item.time,
        mobileNo: item.mobileNo,
        email: item.email,
      };
    }

    const conv = convMap[key];

    // Keep latest status
    if (new Date(item.time) >= new Date(conv.latestTime)) {
      conv.latestStatus = item.status;
      conv.latestIsClose = item.isClose;
      conv.latestTime = item.time;
      conv.clientName = item.clientName || conv.clientName;
    }

    // issueRemark → client message ("them")
    if (item.issueRemark?.trim()) {
      conv.messages.push({
        id: `${item.id}-issue`,
        text: item.issueRemark.trim(),
        sender: "them",
        isoTime: item.time,
        displayTime: fmtTime(item.time),
        displayDate: fmtDate(item.time),
        type: item.type || "",
        isRead: item.isRead ?? false,
      });
    }

    // supporterRemark → our reply ("me") — use actual saved timestamp
    if (item.supporterRemark?.trim()) {
      conv.messages.push({
        id: `${item.id}-support`,
        text: item.supporterRemark.trim(),
        sender: "me",
        isoTime: item.time,
        displayTime: fmtTime(item.time),
        displayDate: fmtDate(item.time),
        type: "",
        isRead: item.isRead ?? false,
      });
    }
  });

  // Sort each conversation's messages chronologically
  Object.values(convMap).forEach((conv) => {
    conv.messages.sort((a, b) => new Date(a.isoTime) - new Date(b.isoTime));
  });

  return Object.values(convMap).sort(
    (a, b) => new Date(b.latestTime) - new Date(a.latestTime)
  );
};

// ── Component ────────────────────────────────────────────────
const ChatPopup = ({ closeChatbot, setTotalUnread }) => {
  const [conversations, setConversations] = useState([]);
  const [activeHash, setActiveHash] = useState(null);
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);
  const msgEndRef = useRef(null);
  const [accountManagerId, setAccountManagerId] = useState(0);

  const [chatDetail, setChatDetail] = useState();

  const wsRef = useRef(null);
  const activeHashRef = useRef(null);
  const suppressRefreshRef = useRef(false);
  const recentlySentRef = useRef(new Set());

  useEffect(() => {
    activeHashRef.current = activeHash;
  }, [activeHash]);

  useEffect(() => {
    const total = conversations.reduce(
      (sum, conv) =>
        sum +
        (conv?.messages?.filter((message) => !message.isRead && message.sender === "them")
          .length || 0),
      0,
    );
    setTotalUnread(total);
  }, [conversations, setTotalUnread]);

  useEffect(() => {
    const stored = localStorage.getItem("authUser");
    if (stored) {
      const parsed = JSON.parse(stored);
      setAccountManagerId(parsed?.id);
    }
  }, []);

  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversations, activeHash]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") closeChatbot();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [closeChatbot]);

  const getUnreadMap = () => {
    return JSON.parse(localStorage.getItem("unreadMap") || "{}");
  };

  const loadConversations = useCallback(async (isBackground = false) => {
    if (!accountManagerId || accountManagerId == "0") return;
    try {
      if (!isBackground) setInitialLoad(true);
      const data = await Support.GetAllSupport();
      const results = data?.Result ?? [];
      const newData = transformResults(results);

      // Auto-mark read for currently open conversation
      const currentHash = activeHashRef.current;
      if (currentHash) {
        const activeConv = newData.find(c => c.hash === currentHash);
        if (activeConv) {
          const unread = activeConv.messages.filter(m => !m.isRead && m.sender === "them");
          if (unread.length) {
            unread
              .filter(m => !isNaN(m.id.split('-')[0].trim()))
              .forEach(m => Support.MarkRead(m.id.split('-')[0].trim()));
            activeConv.messages = activeConv.messages.map(m => ({
              ...m,
              isRead: m.sender === "them" ? true : m.isRead,
            }));
          }
        }
      }

      // ✅ Merge: preserve existing order, just update content
      setConversations(prev => {
        const newDataMap = {};
        newData.forEach(c => (newDataMap[c.hash] = c));

        // Update existing convs in-place (preserve order)
        const updated = prev.map(c =>
          newDataMap[c.hash]
            ? { ...newDataMap[c.hash], unreadCount: c.unreadCount } // keep local unreadCount
            : c
        );

        // Append brand-new convs from API (not yet in local state) at top
        const existingHashes = new Set(prev.map(c => c.hash));
        const brandNew = newData.filter(c => !existingHashes.has(c.hash));

        return [...brandNew, ...updated];
      });

    } catch (error) {
      console.error(error);
    } finally {
      if (!isBackground) setInitialLoad(false);
    }
  }, [accountManagerId]);

  useEffect(() => {
    loadConversations(false);
  }, [loadConversations]);

  // React to SSE notifications (toast is shown in App; this keeps the modal list in sync)
  useEffect(() => {
    let refreshTimer;
    const unsubscribe = onSseNotification((payload) => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        if (suppressRefreshRef.current) return;
        loadConversations(true);
      }, 250);
    });

    return () => {
      clearTimeout(refreshTimer);
      unsubscribe();
    };
  }, [loadConversations]);

  useEffect(() => {
    const map = {};

    conversations.forEach((c) => {
      map[c.hash] = c.unreadCount;
    });

    localStorage.setItem("unreadMap", JSON.stringify(map));
  }, [conversations]);

  // ✅ Socket.IO
  useEffect(() => {
    if (!accountManagerId || accountManagerId === 0) return;

    const socket = io("https://manager.techcherry.in", { // http://localhost:5555
      path: "/socket.io",
      transports: ["polling", "websocket"],
    });
    wsRef.current = socket;

    socket.on("connect", () => {
      console.log("✅ Admin WS connected");
    });

    // ✅ Named event "chat" — data is already a plain object, no JSON.parse needed
    socket.on("chat", (data) => {
      try {
        if (data.success && data.data) {
          const msgData = data.data;
          if (recentlySentRef.current.has(msgData.messages)) {
            recentlySentRef.current.delete(msgData.messages); // consume it
            return;
          }

          if (msgData.flag !== "in") return; // flag "in" = client sends
          if (msgData.senderId === accountManagerId) return
          if (msgData.flag === "in") {
            const now = new Date().toISOString();
            const newMsg = {
              id: `ws-${Date.now()}`,
              text: msgData.messages,
              sender: "them",
              isoTime: msgData.dateTime || now,
              displayTime: fmtTime(msgData.dateTime || now),
              displayDate: fmtDate(msgData.dateTime || now),
              type: "",
              isRead: false,
            };

            const convHash = msgData.hash;

            setConversations((prev) => {
              let updated;

              const existsConv = prev.find((c) => c.hash === convHash);
              if (!existsConv) {
                updated = [
                  {
                    hash: convHash,
                    clientName: msgData.customerName || "New User",
                    concernPerson: "",
                    latestStatus: "Open",
                    latestIsClose: false,
                    messages: [newMsg],
                    latestTime: newMsg.isoTime,
                    mobileNo: msgData.mobileNo,
                    email: "",
                    unreadCount: 1,
                  },
                  ...prev,
                ];
              } else {
                updated = prev.map((conv) => {
                  if (conv.hash !== convHash) return conv;
                  const isActive = conv.hash === activeHashRef.current;
                  return {
                    ...conv,
                    messages: [...conv.messages, newMsg].sort(
                      (a, b) => new Date(a.isoTime) - new Date(b.isoTime),
                    ),
                    unreadCount: isActive ? 0 : (conv.unreadCount || 0) + 1,
                  };
                });
              }

              // Move latest conv to top
              const active = updated.find((c) => c.hash === convHash);
              const rest = updated.filter((c) => c.hash !== convHash);
              return [active, ...rest];
            });
          }
        }
      } catch (e) {
        console.error("Admin WS parse error", e);
      }
    });

    socket.on("connect_error", (err) => console.error("Admin WS Error", err));
    socket.on("disconnect", () => console.log("Admin WS disconnected"));

    return () => {
      socket.disconnect();
    };
  }, [accountManagerId]);

  function getCurrentDateTime() {
    return new Date().toISOString();
  }

  function getCurrentDate() {
    const today = new Date();
    return today.toISOString().split("T")[0];
  }

  const sendMsg = () => {
    if (!input.trim() || !activeHash) return;

    const text = input.trim();
    setInput("");
    recentlySentRef.current.add(text);
    setTimeout(() => recentlySentRef.current.delete(text), 5000);

    suppressRefreshRef.current = true;
    setTimeout(() => { suppressRefreshRef.current = false; }, 3000);

    const now = new Date().toISOString();
    const newMsg = {
      id: `local-${Date.now()}`,
      text: input.trim(),
      sender: "me",
      isoTime: now,
      displayTime: fmtTime(now),
      displayDate: fmtDate(now),
      isRead: false,
    };
    setConversations((prev) => {
      const updated = prev.map((conv) =>
        conv.hash === activeHash
          ? { ...conv, messages: [...conv.messages, newMsg] }
          : conv,
      );
      const active = updated.find((c) => c.hash === activeHash);
      const rest = updated.filter((c) => c.hash !== activeHash);
      return [active, ...rest];
    });
    setInput("");

    if (wsRef.current && wsRef.current.connected) {
      wsRef.current.emit("chat", {
        message: input.trim(),
        recipientId: activeConv?.mobileNo,
        customerName: activeConv?.clientName,
        mobileNo: activeConv?.mobileNo,
        hash: activeHash,
      });
    }

    sendNewMessage();
  };

  const sendNewMessage = async () => {
    const date = await getCurrentDate();
    const datetime = await getCurrentDateTime();
    console.log(chatDetail);

    const payload = {
      clientName: chatDetail?.clientName,
      type: "By Chat",
      givenBy: accountManagerId,
      concernPerson: chatDetail?.concernPerson,
      issueRemark: "",
      date: date,
      time: datetime,
      isClose: chatDetail?.latestIsClose,
      email: chatDetail?.email,
      status: chatDetail?.latestStatus,
      supporterRemark: input,
      mobileNo: chatDetail?.mobileNo,
      hash: chatDetail.hash,
    };
    const data = await Support.CreateSupport(payload);
  };

  const filtered = conversations.filter(
    (c) =>
      c.clientName.toLowerCase().includes(search.toLowerCase()) ||
      c.concernPerson.toLowerCase().includes(search.toLowerCase()),
  );

  const activeConv = conversations.find((c) => c.hash === activeHash);

  // Group messages by display date for date dividers
  const groupedMessages = (() => {
    if (!activeConv) return [];
    const groups = [];
    let lastDate = null;
    activeConv.messages.forEach((msg) => {
      if (msg.displayDate !== lastDate) {
        groups.push({
          type: "divider",
          label: msg.displayDate,
          id: `div-${msg.id}`,
        });
        lastDate = msg.displayDate;
      }
      groups.push({ type: "message", ...msg });
    });
    return groups;
  })();

  const setCurrentChatDetail = (conv) => {
    setActiveHash(conv.hash);
    setChatDetail(conv);

    setConversations((prev) =>
      prev.map((c) => (c.hash === conv.hash ? { ...c, unreadCount: 0 } : c)),
    );
  };

  const handleCurrentChatDetail = async (conv) => {
    console.log(conv);
    setCurrentChatDetail(conv);

    const unreadMessages = conv?.messages?.filter(m => !m.isRead && m.sender === "them");

    if (unreadMessages?.length) {
      const ids = unreadMessages
        .map(item => item.id.split("-")[0].trim())
        .filter(id => !isNaN(id))
        .join(",");

      await Support.MarkRead(ids);

      setConversations(prev =>
        prev.map(c => {
          if (c.hash !== conv.hash) return c;

          return {
            ...c,
            messages: c.messages.map(m => ({
              ...m,
              isRead: true
            }))
          };
        })
      );

      setChatDetail(prev => ({
        ...prev,
        messages: prev.messages.map(m => ({ ...m, isRead: true }))
      }));
    }
  };

  const [openBranchDropdown, setOpenBranchDropdown] = useState(false);
  const [branchData, setBranchData] = useState([]);
  const [branchSearchData, setBranchSearchData] = useState();
  const [searchText, setSearchText] = useState("");

  const handleNewClientSearch = async () => {
    if (!openBranchDropdown) {
      const data = await Branches.GetAllClients(1, 10000);
      const allClients = data.Result || [];
     // const ef2Clients = allClients.filter(item => item.ef2);  // ← filter here
      setBranchData(allClients);
      setBranchSearchData(allClients);
    }
    setOpenBranchDropdown(prev => !prev);
  };

  const debounceRef = useRef(null);

  const handleBranchSearch = (e) => {
    const val = e.target.value;
    setSearchText(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (!val) {
        setBranchSearchData(branchData); // branchData already has ef2 filter
        return;
      }
      const filtered = branchData.filter(item =>
        (item.branchName || "").toLowerCase().includes(val.toLowerCase())
      );
      setBranchSearchData(filtered);
    }, 300);
  };

  useEffect(() => {
    const handleClickOutside = () => setOpenBranchDropdown(false);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleNewBranchSelected = (company) => {
    const conv = {
      clientName: company.branchName,
      concernPerson: company?.accountManagerInfos[0]?.accountManager?.Username,
      email: company.emailId,
      hash: company.hashValue,
      latestIsClose: false,
      latestStatus: "Open",
      latestTime: new Date().toISOString(),
      messages: [],
      mobileNo: company.phoneNo,
      unreadCount: 0,
    };

    setConversations((prev) => {
      const exists = prev.find((c) => c.hash === conv.hash);
      if (exists) {
        return [exists, ...prev.filter((c) => c.hash !== conv.hash)];
      }
      return [conv, ...prev];
    });

    handleCurrentChatDetail(conv);
    setOpenBranchDropdown(false);
  };

  return (
    <div className="chat-popup-overlay" onClick={closeChatbot}>
      <div className={`chat-popup-shell${activeHash ? " chat-popup-shell--thread" : ""}`} onClick={(e) => e.stopPropagation()}>
        {/* ── SIDEBAR ── */}
        <aside className="cp-sidebar">
          <div className="cp-sidebar-header">
            <div className="cp-logo-row">
              <div className="cp-logo-text" style={{ color: "#fff" }}>Messages</div>
              <button
                type="button"
                className="cp-close-btn"
                aria-label="Close chat"
                onClick={closeChatbot}
              >
                ✕
              </button>
            </div>
            <div className="cp-search-wrap">
              <span className="cp-search-icon">🔍</span>
              <input
                className="cp-search"
                placeholder="Search clients..."
                value={searchText}
                onChange={handleBranchSearch}
                onClick={handleNewClientSearch}
              />
              <button className="cp-new-chat-btn" title="New chat" onClick={handleNewClientSearch}>+</button>
              {openBranchDropdown && (
                <div className="cp-dropdown">
                  {branchSearchData?.length > 0 ? (
                    branchSearchData.map((item, i) => (
                      <div
                        key={item.id ?? i}
                        className="cp-dropdown-item"
                        onClick={() => handleNewBranchSelected(item)}
                      >
                        {item.branchName}
                      </div>
                    ))
                  ) : (
                    <div className="cp-dropdown-item">No data</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="cp-section-label">
            {initialLoad ? "Loading..." : `Conversations — ${filtered.length}`}
          </div>

          <div className="cp-users-list">
            {initialLoad ? (
              <div className="cp-loading">
                <div className="cp-spinner" />
                <span>Fetching chats...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="cp-no-results">No conversations found</div>
            ) : (
              filtered //.slice().reverse()
                .map((conv) => {
                  const color = hashColor(conv.hash + conv.clientName);
                  const av = initials(conv.clientName);
                  const lastMsg = conv.messages[conv.messages.length - 1];
                  const dotColor = statusDotColor(
                    conv.latestStatus,
                    conv.latestIsClose,
                  );

                  const unreadCount = conv?.messages?.filter(m => !m.isRead && m.sender === "them").length;

                  return (
                    <div
                      key={conv.hash}
                      className={`cp-user-item${activeHash === conv.hash ? " active" : ""}`}
                      onClick={() => handleCurrentChatDetail(conv)}
                    >
                      <div className="cp-avatar-wrap">
                        <div
                          className="cp-avatar"
                          style={{ background: color + "20", color }}
                        >
                          {av}
                        </div>
                        <div
                          className="cp-status-dot"
                          style={{ background: dotColor }}
                        />
                      </div>
                      <div className="cp-user-info">
                        <div className="cp-user-name">{conv.clientName}</div>
                        <div className="cp-user-preview">
                          {/* {conv.concernPerson && (
                                                    <span className="cp-concern">@{conv.concernPerson} · </span>
                                                )} */}
                          {lastMsg?.text || "No messages yet"}
                        </div>
                      </div>
                      {unreadCount > 0 && (
                        <div className="cp-unread-badge">{unreadCount || 1}</div>
                      )}
                      <div
                        className="cp-status-badge"
                        // style={{ background: dotColor + "22", color: dotColor }}
                        style={{ background: "#e6e9f096", color: "rgb(140 144 153)" }}
                      >
                        {statusDotLabel(conv.latestStatus, conv.latestIsClose)}
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </aside>

        {/* ── MAIN CHAT ── */}
        <main className="cp-main">
          {!activeConv ? (
            <div className="cp-empty">
              <div className="cp-empty-icon">💬</div>
              <div className="cp-empty-text">Select a conversation to view</div>
            </div>
          ) : (
            <>
              {/* Topbar */}
              <div className="cp-topbar">
                <button
                  type="button"
                  className="cp-back-btn"
                  aria-label="Back to conversations"
                  onClick={() => setActiveHash(null)}
                >
                  ←
                </button>
                <div
                  className="cp-avatar"
                  style={{
                    background: 'white',
                    //hashColor(activeConv.hash + activeConv.clientName) + "20",
                    color: hashColor(activeConv.hash + activeConv.clientName),
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.64rem",
                    fontWeight: 700,
                    fontFamily: "DM Mono, monospace",
                    flexShrink: 0,
                  }}
                >
                  {initials(activeConv.clientName)}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="cp-topbar-name">{activeConv.clientName}</div>
                  <div className="cp-topbar-status">
                    <div
                      className="cp-topbar-dot"
                      style={{
                        background: statusDotColor(
                          activeConv.latestStatus,
                          activeConv.latestIsClose,
                        ),
                      }}
                    />
                    {statusDotLabel(
                      activeConv.latestStatus,
                      activeConv.latestIsClose,
                    )}
                    {activeConv.concernPerson && (
                      <span className="cp-topbar-person">
                        {" "}
                        · {activeConv.concernPerson}
                      </span>
                    )}
                  </div>
                </div>
                {/* Issue type tag */}
                {activeConv.messages.find((m) => m.type) && (
                  <div className="cp-type-tag">
                    {activeConv.messages.find((m) => m.type)?.type}
                  </div>
                )}
                <button
                  type="button"
                  className="cp-close-btn"
                  aria-label="Close chat"
                  onClick={closeChatbot}
                >
                  ✕
                </button>
              </div>

              {/* Messages */}
              <div className="cp-messages">
                {groupedMessages.map((item) =>
                  item.type === "divider" ? (
                    // <div key={item.id} className="cp-date-divider">
                    //   {item.label}
                    // </div>
                    <div className="chat-date-separator">
                      <span>{item.label}</span>
                    </div>
                  ) : (
                    <div key={item.id}>
                      <div className={`cp-msg-row ${item.sender}`}>
                        {/* {item.sender === "them" && (
                          <div
                            className="cp-msg-avatar"
                            style={{
                              background:
                                hashColor(
                                  activeConv.hash + activeConv.clientName,
                                ) + "20",
                              color: hashColor(
                                activeConv.hash + activeConv.clientName,
                              ),
                            }}
                          >
                            {initials(activeConv.clientName)}
                          </div>
                        )} */}
                        <div
                          className={`cp-msg-col${item.sender === "me" ? " me" : ""}`}
                        >
                          <div className="cp-bubble">{item.text}</div>
                          <div className="cp-msg-time">
                            {item.displayTime}
                            {item.sender === "me" && (
                              <span
                                className={`cp-tick ${item.isRead ? "cp-tick--read" : "cp-tick--sent"}`}
                                title={item.isRead ? "Read" : "Sent"}
                              >
                                {item.isRead ? "✓✓" : "✓"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                )}
                <div ref={msgEndRef} />
              </div>

              {/* Input */}
              <div className="cp-input-bar">
                <input
                  className="cp-input"
                  placeholder={`Reply to ${activeConv.clientName}...`}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMsg()}
                />
                <button className="cp-send-btn" onClick={sendMsg}>
                  ➤
                </button>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default ChatPopup;
